import { AssistantHistoryMessage, AssistantPendingAction, shovelAiApi } from '@api/shovelAiApi';
import Button from '@components/common/buttons/Button';
import BetaBadge from '@components/common/BetaBadge';
import { softCloseHover } from '@components/common/dialog/softCloseHover';
import MaterialIcon from '@components/common/icons/MaterialIcon';
import { Column, Row } from '@components/common/layoutUtils';
import { usePreviewAccess } from '@hooks/usePreviewAccess';
import colors from '@utils/colors';
import { MediumText, MiniText, RegularText, SmallText } from '@utils/typography';
import { useCallback, useEffect, useRef, useState } from 'react';
import Loader from 'react-loader-spinner';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';

import { getAssistantErrorMessage } from './assistantUtils';
import PendingActionCard from './PendingActionCard';
import { refreshAfterAssistantAction } from './refreshAfterAssistantAction';
import { reportAction } from '@api/activityLogApi';

type ChatMessage = AssistantHistoryMessage;

const NAV_WIDTH = 50;

// Its floating button, which the calendar's Undo moves over for when they meet (Calendar's clearAssistant).
export const ASSISTANT_BUTTON_LABEL = 'Open Shovel Assistant';
const SUCCESS_MESSAGE_MS = 3000;

const ShovelAssistant = () => {
  const dispatch = useDispatch();
  const hasPreviewAccess = usePreviewAccess();

  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [conversationId, setConversationId] = useState<string | undefined>();
  const [pendingActions, setPendingActions] = useState<AssistantPendingAction[]>([]);
  const [dismissedTokens, setDismissedTokens] = useState<Set<string>>(() => new Set());
  const [confirmingTokens, setConfirmingTokens] = useState<Set<string>>(() => new Set());
  const [successByToken, setSuccessByToken] = useState<Record<string, string>>({});
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const visiblePendingActions = pendingActions.filter(action => !dismissedTokens.has(action.confirmationToken));

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, visiblePendingActions.length, isLoading, error]);

  useEffect(() => {
    if (isOpen) {
      inputRef.current?.focus();
    }
  }, [isOpen]);

  const mergePendingActions = useCallback((incoming: AssistantPendingAction[] | undefined) => {
    if (!incoming?.length) {
      return;
    }

    setPendingActions(prev => {
      const existingTokens = new Set(prev.map(action => action.confirmationToken));
      const merged = [...prev];

      incoming.forEach(action => {
        if (!existingTokens.has(action.confirmationToken)) {
          merged.push(action);
        }
      });

      return merged;
    });
  }, []);

  const sendMessage = async () => {
    const trimmed = inputValue.trim();
    if (!trimmed || isLoading) {
      return;
    }

    const history = messages;
    const nextMessages: ChatMessage[] = [...messages, { role: 'user', content: trimmed }];

    setMessages(nextMessages);
    setInputValue('');
    setError(null);
    setIsLoading(true);

    const response = await shovelAiApi.assistantChat({
      message: trimmed,
      conversationId,
      history
    });

    setIsLoading(false);

    if (!response.ok || !response.data) {
      setError(getAssistantErrorMessage(response));
      return;
    }

    const { reply, conversationId: nextConversationId, pendingActions: nextPendingActions } = response.data;

    setConversationId(nextConversationId);
    setMessages(prev => [...prev, { role: 'assistant', content: reply }]);
    mergePendingActions(nextPendingActions);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      sendMessage();
    }
  };

  const dismissAction = (confirmationToken: string) => {
    setDismissedTokens(prev => new Set(prev).add(confirmationToken));
  };

  const confirmAction = async (action: AssistantPendingAction) => {
    const { confirmationToken } = action;

    setConfirmingTokens(prev => new Set(prev).add(confirmationToken));
    setError(null);

    const response = await shovelAiApi.confirmAssistantAction(confirmationToken);

    setConfirmingTokens(prev => {
      const next = new Set(prev);
      next.delete(confirmationToken);
      return next;
    });

    if (!response.ok || !response.data?.success) {
      setError(getAssistantErrorMessage(response));
      return;
    }

    refreshAfterAssistantAction(dispatch, response.data.actionType, response.data.result);

    setSuccessByToken(prev => ({
      ...prev,
      [confirmationToken]: 'Action completed successfully.'
    }));

    setTimeout(() => {
      setSuccessByToken(prev => {
        const next = { ...prev };
        delete next[confirmationToken];
        return next;
      });
      setDismissedTokens(prev => new Set(prev).add(confirmationToken));
    }, SUCCESS_MESSAGE_MS);
  };

  const closeChat = () => {
    setIsOpen(false);
  };

  if (!hasPreviewAccess) {
    return null;
  }

  return (
    <Root>
      {isOpen && (
        <ChatWindow>
          <Header>
            <TitleRow>
              <MediumText strong>Shovel Assistant</MediumText>
              <BetaBadge>Beta</BetaBadge>
            </TitleRow>
            <HeaderActions>
              <HeaderButton onClick={closeChat} aria-label="Minimize assistant">
                <MaterialIcon name="remove" size={20} />
              </HeaderButton>
              <HeaderButton onClick={closeChat} aria-label="Close assistant">
                <MaterialIcon name="close" size={20} />
              </HeaderButton>
            </HeaderActions>
          </Header>

          <Thread>
            {messages.length === 0 && !isLoading && (
              <EmptyState>
                <RegularText color={colors.gray}>Ask about your courses, tasks, schedule, or workload.</RegularText>
              </EmptyState>
            )}

            {messages.map((message, index) => (
              <MessageBubble key={`${message.role}-${index}`} role={message.role}>
                <SmallText>{message.content}</SmallText>
              </MessageBubble>
            ))}

            {visiblePendingActions.map(action => (
              <PendingActionCard
                key={action.confirmationToken}
                action={action}
                confirming={confirmingTokens.has(action.confirmationToken)}
                successMessage={successByToken[action.confirmationToken]}
                onConfirm={() => confirmAction(action)}
                onDismiss={() => dismissAction(action.confirmationToken)}
              />
            ))}

            {isLoading && (
              <LoadingRow>
                <Loader type="Oval" color={colors.primaryPurple} height={16} width={16} />
                <MiniText color={colors.gray}>Thinking...</MiniText>
              </LoadingRow>
            )}

            {error && <ErrorText>{error}</ErrorText>}

            <div ref={messagesEndRef} />
          </Thread>

          <InputArea>
            <MessageInput
              ref={inputRef}
              value={inputValue}
              onChange={event => setInputValue(event.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Ask Shovel Assistant..."
              rows={1}
              disabled={isLoading}
            />
            <SendButton filled onClick={sendMessage} disabled={isLoading || !inputValue.trim()}>
              Send
            </SendButton>
          </InputArea>
        </ChatWindow>
      )}

      <Fab
        onClick={() => {
          if (!isOpen) reportAction('assistant_opened');
          setIsOpen(prev => !prev);
        }}
        aria-label={ASSISTANT_BUTTON_LABEL}
      >
        <MaterialIcon name="auto_awesome" size={24} color={colors.white} />
      </Fab>
    </Root>
  );
};

export default ShovelAssistant;

const Root = styled.div`
  position: fixed;
  bottom: 24px;
  left: ${NAV_WIDTH + 24}px;
  z-index: 100;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 12px;
`;

const Fab = styled.button`
  width: 56px;
  height: 56px;
  border: none;
  border-radius: 50%;
  background-color: ${colors.primaryPurple};
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.18);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.15s ease, box-shadow 0.15s ease;

  &:hover {
    transform: scale(1.04);
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.22);
  }
`;

const ChatWindow = styled(Column)`
  width: 400px;
  height: 560px;
  border-radius: 12px;
  overflow: hidden;
  background-color: ${({ theme }) => theme.background};
  box-shadow: 0 8px 32px ${({ theme }) => theme.boxShadow};
  border: 1px solid ${({ theme }) => theme.borderColor};
`;

const Header = styled(Row)`
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  border-bottom: 1px solid ${({ theme }) => theme.borderColor};
  background-color: ${({ theme }) => theme.backgroundLight};
`;

const TitleRow = styled(Row)`
  gap: 8px;
  align-items: center;
`;

const HeaderActions = styled(Row)`
  gap: 4px;
`;

const HeaderButton = styled.button`
  border: none;
  background: transparent;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  ${softCloseHover({ over: 'transparent', spread: 0 })}
`;

const Thread = styled(Column)`
  flex: 1;
  overflow-y: auto;
  padding: 16px;
  gap: 10px;
`;

const EmptyState = styled.div`
  padding: 8px 0;
`;

const MessageBubble = styled.div<{ role: ChatMessage['role'] }>`
  align-self: ${props => (props.role === 'user' ? 'flex-end' : 'flex-start')};
  max-width: 85%;
  padding: 10px 12px;
  border-radius: 12px;
  background-color: ${props => (props.role === 'user' ? colors.primaryPurple : props.theme.backgroundLight)};
  color: ${props => (props.role === 'user' ? colors.white : props.theme.textColor)};

  span {
    color: inherit;
    white-space: pre-wrap;
    word-break: break-word;
  }
`;

const LoadingRow = styled(Row)`
  gap: 8px;
  align-items: center;
`;

const ErrorText = styled(RegularText)`
  color: ${colors.negative};
`;

const InputArea = styled(Row)`
  gap: 8px;
  padding: 12px;
  border-top: 1px solid ${({ theme }) => theme.borderColor};
  align-items: flex-end;
`;

const MessageInput = styled.textarea`
  flex: 1;
  resize: none;
  min-height: 40px;
  max-height: 100px;
  padding: 10px 12px;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.borderColor};
  background-color: ${({ theme }) => theme.backgroundLight};
  color: ${({ theme }) => theme.textColor};
  font-family: inherit;
  font-size: 14px;
  line-height: 1.4;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: ${colors.primaryPurple};
  }

  &:disabled {
    opacity: 0.6;
  }
`;

const SendButton = styled(Button)`
  min-width: 72px;
`;
